import { useEffect, useMemo, useCallback, useState, useRef } from 'react';
import { useSelector } from 'react-redux';

import { useLanguage } from '../../../../contexts/LanguageContext';
import { usePermissions } from '../../../../hooks/usePermissions';
import { selectUserParams } from '../../../../store/slices/userParamsSlice';
import { useGetChassisDropdownListQuery, useGetLookUpDropdownListQuery, useLazyGetChassisDetailQuery } from '../../../../api';
import {
    useGetTechnicalSupportRequestPreviousKmQuery,
    useCreateTechnicalSupportRequestMutation,
    useUpdateTechnicalSupportRequestMutation,
    useAddCommentMutation,
    useUploadTechnicalSupportRequestDocumentMutation,
    useGetTechnicalSupportRequestDocumentsByIdQuery,
    useDeleteTechnicalSupportRequestDocumentMutation,
    useLazyDownloadAllTechnicalSupportRequestDocumentsQuery,
    useUploadExpenseDocumentMutation,
    useGetExpenseDocumentsByIdQuery,
    useDeleteExpenseDocumentMutation,
    useSaveFeedbackAnswersMutation,
    useGetTechnicalSupportResponsiblesQuery,
    useLazyGetTechnicalSupportVehicleModelYearByChassisNoQuery
} from '../../../../api/endpoints/technicalSupportRequest';
import { useLazyDownloadDocumentByKeyQuery } from '../../../../api/endpoints/documentContent';
import { useLazyGetVehicleWarrantyInformationQuery } from '../../../../api/endpoints/vehicleWarrantyInformationReport';

import toastService from '../../../../utils/toastService';
import DocumentDownloadService from '../../../../utils/documentDownloadService';

import { useFormState } from './useFormState';
import { useValidation } from './useValidation';
import { useFormDataMapper } from './useFormDataMapper';
import { getCreatedCommentId, getDocId, getFileKey, normalizeDocumentGroups, normalizeDocumentList } from '../utils/documentUtils';
import { isEmptyHtmlContent } from '../../../../utils/richTextUtils';
import { WARRANTY_IN, WARRANTY_OUT, getWarrantyRecords, getDateEligibleWarranties, resolveVehicleModelFromChassis } from '../utils/warrantyUtils';

const getStatusSnapshot = (form) => ({
    statusNotification: form.statusNotification,
    statusDate: form.statusDate,
    statusChanged: form.statusChanged,
    solutionDate: form.solutionDate
});

const useTechnicalSupportRequestFormData = ({ dialogVisible, editMode, request, onSaveSuccess, onRefreshRequest, isVorMode = false, requireDealerAddressSelection = false }) => {
    const { th, getCurrentLanguageId } = useLanguage();
    const userParams = useSelector(selectUserParams);
    const { hasAdminRole, hasSSHManagerRole, hasDealerRole, hasServiceDealerRole, hasSalesDealerRole, hasWarrantyQualityOfficerRole, hasKarsanSSHRole, hasTechnicalSupportUserRole } = usePermissions();

    const isDealer = hasDealerRole() || hasServiceDealerRole() || hasSalesDealerRole() || hasTechnicalSupportUserRole();
    const [documentTabIndex, setDocumentTabIndex] = useState(0);
    const isWarrantyQualityOfficer = hasWarrantyQualityOfficerRole();
    const isKarsanSSH = hasKarsanSSHRole();
    const shouldShowDealerAddressSelection = isVorMode || requireDealerAddressSelection;

    const [createRequest] = useCreateTechnicalSupportRequestMutation();
    const [updateRequest] = useUpdateTechnicalSupportRequestMutation();
    const [isSaving, setIsSaving] = useState(false);
    const [addComment, { isLoading: isAddingComment }] = useAddCommentMutation();
    const [uploadExpenseDocument] = useUploadExpenseDocumentMutation();
    const [deleteExpenseDocument] = useDeleteExpenseDocumentMutation();
    const [expenseFiles, setExpenseFiles] = useState([]);
    const [uploadDocument] = useUploadTechnicalSupportRequestDocumentMutation();
    const [deleteDocument] = useDeleteTechnicalSupportRequestDocumentMutation();
    const [downloadAllDocuments, { isFetching: isDownloadingDocuments }] = useLazyDownloadAllTechnicalSupportRequestDocumentsQuery();
    const [triggerDownloadByKey] = useLazyDownloadDocumentByKeyQuery();
    const toastRef = useRef(null);
    const [downloadService] = useState(() => new DocumentDownloadService(toastRef));
    const [triggerGetVehicleWarrantyInformation, { isFetching: isFetchingWarrantyInformation }] = useLazyGetVehicleWarrantyInformationQuery();
    const [triggerGetChassisDetail] = useLazyGetChassisDetailQuery();
    const [triggerGetVehicleModelYear] = useLazyGetTechnicalSupportVehicleModelYearByChassisNoQuery();
    const [isVehicleWarrantyInfLocked, setIsVehicleWarrantyInfLocked] = useState(false);
    const [isVehicleModelLocked, setIsVehicleModelLocked] = useState(false);

    const technicalPolicyRef = useRef(null);
    const additionalWarrantyPolicyRef = useRef(null);

    const [additionalChassisSearch, setAdditionalChassisSearch] = useState('');
    const [debouncedChassisSearch, setDebouncedChassisSearch] = useState('');

    const { data: expenseDocumentsRaw, isLoading: isLoadingExpenseDocuments, refetch: refetchExpenseDocuments } = useGetExpenseDocumentsByIdQuery(request?.id, { skip: !request?.id || isDealer });

    const expenseDocuments = useMemo(() => normalizeDocumentList(expenseDocumentsRaw, dialogVisible, request?.id), [expenseDocumentsRaw, dialogVisible, request?.id]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedChassisSearch(additionalChassisSearch);
        }, 400);
        return () => clearTimeout(timer);
    }, [additionalChassisSearch]);

    const { data: documentsRaw, isLoading: isLoadingDocuments, refetch: refetchDocuments } = useGetTechnicalSupportRequestDocumentsByIdQuery(request?.id, { skip: !request?.id });

    const documentGroups = useMemo(() => normalizeDocumentGroups(documentsRaw, dialogVisible, request?.id), [documentsRaw, dialogVisible, request?.id]);
    const { dealerCustomerFiles, technicalTeamFiles } = documentGroups;

    const [updatedFiles, setUpdatedFiles] = useState([]);

    useEffect(() => {
        if (dialogVisible) {
            setDocumentTabIndex(0);
        }
    }, [dialogVisible]);

    const currentLanguageId = getCurrentLanguageId();
    const { data: currentStatusOptions = [] } = useGetLookUpDropdownListQuery({ LanguageId: currentLanguageId, GroupCode: 'TECHNICAL_CURRENT_STATUS' });
    const releasedLabel = th('technicalSupport.fields.vehicleReleasedStatus').trim().toLocaleLowerCase();
    const releasedOption = currentStatusOptions.find((item) => [item.translatedText, item.text].some((label) => label?.trim().toLocaleLowerCase() === releasedLabel));
    const releasedCode = Number(releasedOption?.code ?? releasedOption?.lookupCode);
    const releasedStatusCode = releasedOption && Number.isFinite(releasedCode) ? releasedCode : null;
    const { form, setField, setFields, resetForm } = useFormState();
    const changeDateRequired = form.statusChanged || form.actionManagerChanged;
    const isReleasedStatus = releasedStatusCode !== null && form.statusNotification === releasedStatusCode;
    const selectedStatusDateRef = useRef(null);
    const problemSolvedRef = useRef(null);
    const handleStatusChange = (value) => {
        if (Number(value) === Number(form.statusNotification)) return;
        const statusChanged = editMode ? Number(value) !== Number(request?.statusNotification) : value !== null;
        selectedStatusDateRef.current = null;
        const patch = { statusNotification: value, statusChanged, statusDate: form.actionManagerChanged ? form.statusDate : null };

        if (!statusChanged) {
            Object.assign(patch, {
                problemSolved: editMode && (request?.problemSolved === true || request?.problemSolved === 1),
                solutionDate: editMode && request?.solutionDate ? new Date(request.solutionDate) : null,
                solutionDateFromStatus: false,
                statusBeforeSolved: null
            });
        } else if (releasedStatusCode !== null && value === releasedStatusCode) {
            Object.assign(patch, {
                problemSolved: true,
                solutionDate: null,
                solutionDateFromStatus: true,
                statusBeforeSolved: form.problemSolved ? form.statusBeforeSolved : getStatusSnapshot(form)
            });
        } else if (isReleasedStatus) {
            Object.assign(patch, { problemSolved: false, solutionDate: null, solutionDateFromStatus: false, statusBeforeSolved: null });
        }

        setFields(patch);
    };

    const handleActionManagerChange = (value) => {
        const changed = editMode ? Number(value) !== Number(request?.actionManager) : value !== null;
        const statusDate = form.statusChanged ? form.statusDate : changed ? null : request?.statusDate ? new Date(request.statusDate) : null;
        selectedStatusDateRef.current = null;
        setFields({ actionManager: value, actionManagerChanged: changed, statusDate });
    };

    const handleProblemSolvedChange = (value) => {
        if (!value) {
            selectedStatusDateRef.current = null;
            const needsAnotherStatus = !form.statusBeforeSolved && isReleasedStatus;
            setFields({
                problemSolved: false,
                solutionDateFromStatus: false,
                solutionDate: null,
                ...(form.statusBeforeSolved || {}),
                ...(needsAnotherStatus ? { statusNotification: null, statusChanged: true, statusDate: null } : {}),
                statusBeforeSolved: null
            });
            return;
        }

        if (releasedStatusCode === null) {
            toastService.error(th('technicalSupport.messages.unknownError'));
            return;
        }

        const statusChanged = editMode ? Number(request?.statusNotification) !== releasedStatusCode : true;
        setFields({
            problemSolved: true,
            statusNotification: releasedStatusCode,
            statusChanged,
            statusDate: form.solutionDate,
            solutionDateFromStatus: false,
            statusBeforeSolved: form.statusBeforeSolved || getStatusSnapshot(form)
        });
    };

    const scrollToProblemSolved = () => {
        if (!(selectedStatusDateRef.current || form.statusDate) || !isReleasedStatus) return;
        const target = problemSolvedRef.current;
        const dialogContent = target?.closest('.p-dialog-content');
        if (!target || !dialogContent) return;
        dialogContent.scrollTo({ top: dialogContent.scrollTop + target.getBoundingClientRect().top - dialogContent.getBoundingClientRect().top - 80, behavior: 'smooth' });
    };

    const handleStatusDateChange = (value) => {
        const statusDate = value ? new Date(value) : null;
        if (statusDate) {
            const now = new Date();
            statusDate.setHours(now.getHours(), now.getMinutes(), now.getSeconds(), 0);
        }
        selectedStatusDateRef.current = statusDate;
        setFields({ statusDate, ...(isReleasedStatus && form.statusChanged ? { solutionDate: statusDate } : {}) });
    };

    const handleSolutionDateChange = (value) => {
        const statusDate = value ? new Date(value) : null;
        if (statusDate) {
            const time = form.statusDate || new Date();
            statusDate.setHours(time.getHours(), time.getMinutes(), time.getSeconds(), 0);
        }
        selectedStatusDateRef.current = statusDate;
        setFields({ solutionDate: value, ...(isReleasedStatus ? { statusDate } : {}) });
    };
    const isFormInitializedRef = useRef(false);
const modelYearRequestRef = useRef(null);

const handleChassisChange = useCallback(
    async (chassisId) => {
        const chassisNo = String(chassisId || '').trim();

        modelYearRequestRef.current?.abort();
        setFields({ chassisNo: chassisId || '', modelYear: '' });

        if (!chassisNo) return;

        const request = triggerGetVehicleModelYear(chassisNo);
        modelYearRequestRef.current = request;

        try {
            const modelYear = await request.unwrap();
            if (modelYearRequestRef.current === request)
                setField('modelYear', modelYear ?? '');
        } catch {
            // Ignore errors, as the request may be aborted or fail for other reasons.
        }
    },
    [setField, setFields, triggerGetVehicleModelYear]
);
    const chassisNo = String(form.chassisNo || '').trim();
    const {
        currentData: previousKmResponse,
        isFetching: isFetchingPreviousKm,
        isError: isPreviousKmError
    } = useGetTechnicalSupportRequestPreviousKmQuery(
        {
            chassisNo,
            menzilType: form.menzilType,
            excludeRequestId: editMode ? request?.id ?? request?.Id : undefined
        },
        {
            skip: !dialogVisible || !chassisNo || !form.menzilType,
            refetchOnMountOrArgChange: true
        }
    );

    const shouldCheckPreviousKm =
        !editMode || chassisNo !== String(form.initialChassisNo || '').trim() || Number(form.menzilType) !== Number(request?.menzilType ?? request?.MenzilType ?? 1) || Number(form.kmInformation) !== Number(request?.kmInformation);
    const previousKm = !isPreviousKmError && dialogVisible && chassisNo && previousKmResponse && previousKmResponse.isSuccessful !== false ? previousKmResponse.data ?? 0 : null;
    const formattedPreviousKm = previousKm != null ? Number(previousKm).toLocaleString('en-US') : null;
    const previousKmUnit = form.menzilType === 2 ? 'Mil' : 'KM';
    const isKmBelowPrevious = shouldCheckPreviousKm && previousKm != null && form.kmInformation != null && Number(form.kmInformation) < Number(previousKm);

    const selectedSiteUseId = useMemo(() => {
        const selectedAddress = form.selectedDealerAddress;
        return selectedAddress?.id ?? selectedAddress?.addrId ?? selectedAddress?.erpSiteUseId ?? (typeof selectedAddress === 'string' || typeof selectedAddress === 'number' ? selectedAddress : null);
    }, [form.selectedDealerAddress]);

    const { currentData: technicalResponsibles = [], isFetching: isFetchingTechnicalResponsibles } = useGetTechnicalSupportResponsiblesQuery(selectedSiteUseId, {
        skip: !dialogVisible || !shouldShowDealerAddressSelection || !selectedSiteUseId
    });

    const technicalResponsibleOptions = useMemo(
        () =>
            technicalResponsibles.map((responsible) => {
                const userId = Number(responsible.userId);
                const responsibilityOrder = responsible.responsibilityOrder;
                let label = responsible.userName || '';

                if (Number(responsibilityOrder) === 1) {
                    label = `${label} - ${th('technicalSupport.responsibilityOrder.primary')}`;
                } else if (responsibilityOrder !== null && responsibilityOrder !== undefined) {
                    label = `${label} - ${th('technicalSupport.responsibilityOrder.ordered', { order: responsibilityOrder })}`;
                }

                return { label, value: userId };
            }),
        [technicalResponsibles, th]
    );

    const existingTechnicalResponsibleUserId = request?.technicalResponsibleUserId ?? request?.TechnicalResponsibleUserId ?? null;
    const canEditTechnicalResponsible = !existingTechnicalResponsibleUserId || hasAdminRole() || hasSSHManagerRole();

    const isReadOnly = (isDealer || isWarrantyQualityOfficer || isKarsanSSH) && editMode && isFormInitializedRef.current && (request?.problemSolved === true || request?.problemSolved === 1);

    const [feedbackVisible, setFeedbackVisible] = useState(false);
    const [pendingPayload, setPendingPayload] = useState(null);
    const [saveFeedbackAnswers] = useSaveFeedbackAnswersMutation();

    const { data: additionalChassisList = [], isFetching: isFetchingAdditionalChassis } = useGetChassisDropdownListQuery(
        {
            RowCount: 50,
            SearchText: debouncedChassisSearch,
            languageId: currentLanguageId
        },
        {
            skip: !dialogVisible || !form.moreChassis
        }
    );

    const { data: errorEffectLookupOptions = [] } = useGetLookUpDropdownListQuery({
        LanguageId: currentLanguageId,
        GroupCode: 'ERROR_EFFECT'
    });
    const { data: vehicleEmissionLookupOptions = [] } = useGetLookUpDropdownListQuery({
        LanguageId: currentLanguageId,
        GroupCode: 'VEHICLE_EMISSIONS'
    });
    const { data: selectedChassisCandidates = [], isFetching: isFetchingSelectedChassis } = useGetChassisDropdownListQuery(
        {
            RowCount: 50,
            SearchText: String(form.chassisNo || '').trim(),
            languageId: currentLanguageId
        },
        {
            skip: !dialogVisible || !form.chassisNo
        }
    );

    const { validate, isKmInvalid } = useValidation(form, th, isDealer, isVorMode, editMode);
    const { mapRequestToForm, mapFormToPayload } = useFormDataMapper();

    const warrantyOptions = useMemo(
        () => [
            { label: th('technicalSupport.optionLabels.warrantyInWarranty'), value: 1 },
            { label: th('technicalSupport.optionLabels.warrantyOutOfWarranty'), value: 2 }
        ],
        [th]
    );

    const gsrOptions = useMemo(
        () => [
            { label: th('common.yes'), value: 1 },
            { label: th('common.no'), value: 0 }
        ],
        [th]
    );

    const menzilTypeOptions = useMemo(
        () => [
            { label: 'KM', value: 1 },
            { label: 'Mil', value: 2 }
        ],
        []
    );

    const errorEffectOptions = useMemo(
        () =>
            (errorEffectLookupOptions || [])
                .map((item) => {
                    const rawValue = item?.code ?? item?.lookupCode;
                    const numericValue = Number(rawValue);
                    return {
                        label: item?.translatedText || item?.text || String(rawValue),
                        value: Number.isFinite(numericValue) ? numericValue : null
                    };
                })
                .filter((item) => item.value !== null),
        [errorEffectLookupOptions]
    );

    const selectedErrorEffectInfo = useMemo(() => {
        if (!form.errorEffect) return '';
        const translationKey = `technicalSupport.errorEffectInfo.${form.errorEffect}`;
        const translatedText = th(translationKey);

        if (translatedText && translatedText !== translationKey) {
            return translatedText;
        }

        const selectedOption = errorEffectOptions.find((option) => option.value === form.errorEffect);
        return selectedOption?.label || '';
    }, [form.errorEffect, th, errorEffectOptions]);

    const additionalChassisOptions = useMemo(() => {
        return additionalChassisList.map((c) => {
            const value = c?.chassisNo ?? c?.id ?? c;
            const label = c?.chassisNo ?? c?.id ?? String(c);
            return { label, value };
        });
    }, [additionalChassisList]);

    const isVisitDateAfterErrorNotificationInvalid = useMemo(() => {
        if (!form.onSiteSupport || isDealer || !form.visitDate || !form.errorNotificationDate) {
            return false;
        }

        const visitDate = new Date(form.visitDate);
        const errorNotificationDate = new Date(form.errorNotificationDate);

        if (Number.isNaN(visitDate.getTime()) || Number.isNaN(errorNotificationDate.getTime())) {
            return true;
        }

        visitDate.setHours(0, 0, 0, 0);
        errorNotificationDate.setHours(0, 0, 0, 0);

        return visitDate.getTime() <= errorNotificationDate.getTime();
    }, [form.onSiteSupport, form.visitDate, form.errorNotificationDate, isDealer]);

    const isSolutionDateAfterErrorNotificationInvalid = useMemo(() => {
        if (!form.problemSolved || !form.solutionDate || !form.errorNotificationDate) {
            return false;
        }

        const solutionDate = new Date(form.solutionDate);
        const errorNotificationDate = new Date(form.errorNotificationDate);

        if (Number.isNaN(solutionDate.getTime()) || Number.isNaN(errorNotificationDate.getTime())) {
            return true;
        }

        solutionDate.setHours(0, 0, 0, 0);
        errorNotificationDate.setHours(0, 0, 0, 0);

        return solutionDate.getTime() < errorNotificationDate.getTime();
    }, [form.problemSolved, form.solutionDate, form.errorNotificationDate]);

    const vehicleEmissionOptions = useMemo(
        () =>
            (vehicleEmissionLookupOptions || [])
                .map((item) => {
                    const rawValue = item?.code ?? item?.lookupCode;
                    const numericValue = Number(rawValue);
                    return {
                        label: item?.translatedText || item?.text || String(rawValue),
                        value: Number.isFinite(numericValue) ? numericValue : null
                    };
                })
                .filter((item) => item.value !== null),
        [vehicleEmissionLookupOptions]
    );

    const onEditFilesChange = (files) => setUpdatedFiles(files || []);

    const uploadFiles = useCallback(
        async (requestId) => {
            if (!requestId || updatedFiles.length === 0) return true;

            const failedFiles = [];
            for (const item of updatedFiles) {
                const f = item?.rawFile || item?.file || item;
                try {
                    await uploadDocument({
                        TechnicalSupportRequestId: requestId,
                        file: f,
                        description: f?.name || item?.name || 'attachment'
                    }).unwrap();
                } catch (error) {
                    failedFiles.push(item);
                    toastService.error(error?.data?.errorMessage || th('technicalSupport.messages.uploadDocumentError'));
                }
            }

            if (request?.id) {
                try {
                    await refetchDocuments();
                } catch {
                    // Query henüz başlamadıysa veya başka bir sebepten sessizce geç
                }
            }

            setUpdatedFiles(failedFiles);
            return failedFiles.length === 0;
        },
        [updatedFiles, uploadDocument, th, request?.id, refetchDocuments]
    );

    const onDeleteDocument = useCallback(
        async (doc) => {
            const docId = getDocId(doc);
            const requestId = request?.id ?? request?.Id ?? null;

            if (!requestId || !docId) {
                toastService.error(th('technicalSupport.messages.missingFileInfo'));
                return;
            }

            const fileName = doc.fileName || doc.name || 'File';
            const confirmMessage = th('technicalSupport.deleteDocumentConfirm', { fileName });

            if (!window.confirm(confirmMessage)) return;

            const payload = {
                Id: Number(requestId),
                DocumentId: Number(docId)
            };

            try {
                await deleteDocument(payload).unwrap();
                await refetchDocuments();
                toastService.success(th('technicalSupport.messages.deleteDocumentSuccess'));
            } catch (error) {
                toastService.error(error?.data?.errorMessage || th('technicalSupport.messages.deleteDocumentError'));
            }
        },
        [request, th, deleteDocument, refetchDocuments]
    );

    const onDownloadDocument = useCallback(
        async (doc) => {
            const key = getFileKey(doc);
            if (!key) {
                return;
            }
            if (toastRef?.current) {
                toastRef.current.show({
                    severity: 'info',
                    summary: th('common.downloading'),
                    detail: th('common.downloading'),
                    life: 3000
                });
            }
            try {
                await downloadService.downloadByKey(triggerDownloadByKey, key, doc, {
                    successMessage: th('technicalSupport.messages.downloadDocumentSuccess'),
                    errorMessage: th('technicalSupport.messages.downloadDocumentError')
                });
            } catch {
                toastService.error(th('technicalSupport.messages.downloadDocumentError'));
            }
        },
        [th, downloadService, triggerDownloadByKey]
    );

    const onDownloadAllDocuments = useCallback(
        async (documentGroup) => {
            if (!request?.id) return;

            try {
                const result = await downloadAllDocuments({ technicalSupportRequestId: request.id, documentGroup }).unwrap();
                const fileName = result.contentDisposition?.match(/filename="?([^";]+)"?/i)?.[1] || `technical-support-${request.id}-documents.zip`;
                downloadService.downloadBlob(result.blob, fileName, false);
                toastService.success(th('technicalSupport.messages.downloadDocumentSuccess'));
            } catch {
                toastService.error(th('technicalSupport.messages.downloadDocumentError'));
            }
        },
        [downloadAllDocuments, downloadService, request?.id, th]
    );

    const checkChassisWarranty = useCallback(
        async (chassisNo, errorNotificationDate) => {
            const normalizedChassisNo = String(chassisNo || '').trim();

            try {
                if (!normalizedChassisNo || !errorNotificationDate) {
                    return false;
                }

                const warrantyResponse = await triggerGetVehicleWarrantyInformation({
                    sasiNo: normalizedChassisNo,
                    pageNumber: 1,
                    pageSize: 500
                }).unwrap();
                const warrantyItems = Array.isArray(warrantyResponse?.items) ? warrantyResponse.items : [];

                let lastKmResult;
                const getLastKm = async () => {
                    if (lastKmResult) {
                        return lastKmResult;
                    }

                    const chassisDetail = await triggerGetChassisDetail({ chassisNumber: normalizedChassisNo }).unwrap();
                    const detailChassisNo = String(chassisDetail?.chassisNumber || '').trim();
                    const rawLastKmInfo = chassisDetail?.lastKmInfo;
                    const lastKmInfo = rawLastKmInfo === null || rawLastKmInfo === undefined || rawLastKmInfo === '' ? NaN : Number(rawLastKmInfo);
                    const isValid = detailChassisNo.toUpperCase() === normalizedChassisNo.toUpperCase() && Number.isFinite(lastKmInfo);

                    lastKmResult = { lastKmInfo, isValid };
                    return lastKmResult;
                };

                const evaluateMileage = (records, lastKmInfo) =>
                    records.some((record) => {
                        const rawMileageLimit = record?.mileage ?? record?.Mileage;
                        const mileageLimit = rawMileageLimit === null || rawMileageLimit === undefined || rawMileageLimit === '' ? NaN : Number(rawMileageLimit);
                        return Number.isFinite(mileageLimit) && lastKmInfo <= mileageLimit;
                    });

                const standardRecords = getWarrantyRecords(warrantyItems, normalizedChassisNo, 1);
                const standardDateEligible = getDateEligibleWarranties(standardRecords, errorNotificationDate);

                if (standardDateEligible.length > 0) {
                    const standardKmResult = await getLastKm();
                    if (standardKmResult.isValid && evaluateMileage(standardDateEligible, standardKmResult.lastKmInfo)) {
                        return true;
                    }
                }

                const extendedRecords = getWarrantyRecords(warrantyItems, normalizedChassisNo, 2);
                const extendedDateEligible = getDateEligibleWarranties(extendedRecords, errorNotificationDate);

                if (extendedDateEligible.length === 0) {
                    return false;
                }

                const extendedKmResult = await getLastKm();
                return extendedKmResult.isValid && evaluateMileage(extendedDateEligible, extendedKmResult.lastKmInfo);
            } catch {
                return false;
            }
        },
        [triggerGetChassisDetail, triggerGetVehicleWarrantyInformation]
    );

    const checkInitialAdditionalChassisWarranty = useCallback(
        async (chassisList, errorNotificationDate, alreadyAccepted) => {
            for (const chassisNo of chassisList) {
                const isInWarranty = await checkChassisWarranty(chassisNo, errorNotificationDate);

                if (!isInWarranty) {
                    setFields({
                        additionalChassisHasOutOfWarranty: true,
                        warrantyPolicyAccepted: alreadyAccepted
                    });
                    return;
                }
            }
            setFields({
                additionalChassisHasOutOfWarranty: false
            });
        },
        [checkChassisWarranty, setFields]
    );

    useEffect(() => {
        if (dialogVisible && request?.id) {
            refetchDocuments();
        }
    }, [dialogVisible, request?.id, refetchDocuments]);

    useEffect(() => {
        if (!dialogVisible) {
            selectedStatusDateRef.current = null;
            modelYearRequestRef.current?.abort();
            modelYearRequestRef.current = null;
            resetForm();
            setUpdatedFiles([]);
            setExpenseFiles([]);
            isFormInitializedRef.current = false;
            return;
        }

        if (request) {
            if (!isFormInitializedRef.current) {
                const formData = mapRequestToForm(request, userParams);

                if (formData) {
                    setFields(formData);
                    isFormInitializedRef.current = true;
                    const chassisList = Array.isArray(request.chassisList) ? request.chassisList : [];
                    if (chassisList.length > 0) {
                        checkInitialAdditionalChassisWarranty(chassisList, formData.errorNotificationDate, request.warrantyPolicy === 1);
                    }
                }
            } else {
                const comments = request.technicalSupportComments || [];
                const mappedChat = comments.map((comment) => ({
                    id: comment.id,
                    createdBy: comment.createdBy ?? comment.CreatedBy ?? comment.createdUserId ?? comment.CreatedUserId ?? null,
                    userName: comment.createdName || 'Unknown',
                    message: comment.comment || '',
                    timestamp: comment.createdDate || new Date().toISOString(),
                    type: comment.type || 1
                }));
                setField('chatHistory', mappedChat);
            }
        } else if (!isFormInitializedRef.current) {
            const patch = {
                submitted: false,
                userName: userParams?.userEmail || '',
                dealerName: userParams?.dealerFullName || ''
            };

            if (isDealer) {
                patch.statusNotification = 1;
                patch.actionManager = 1;
            }

            if (isVorMode) {
                patch.isVor = true;
            }

            setFields(patch);
            isFormInitializedRef.current = true;
        }
    }, [dialogVisible, request, resetForm, setFields, setField, userParams, isDealer, isVorMode, mapRequestToForm, checkInitialAdditionalChassisWarranty]);

    useEffect(() => {
        if (!dialogVisible) {
            setIsVehicleWarrantyInfLocked(false);
            setIsVehicleModelLocked(false);
            return;
        }

        const chassisNo = String(form.chassisNo || '').trim();
        if (!chassisNo) {
            setIsVehicleWarrantyInfLocked(false);
            setIsVehicleModelLocked(false);
            return;
        }

        let isCancelled = false;
        const resolveWarrantyInfo = async () => {
            const isInWarranty = await checkChassisWarranty(chassisNo, form.errorNotificationDate);

            if (isCancelled) return;

            const chassisChanged = editMode && chassisNo !== form.initialChassisNo;
            const shouldOpenDialog = !editMode || chassisChanged;
            const warrantyPatch = {
                vehicleWarrantyInf: isInWarranty ? WARRANTY_IN : WARRANTY_OUT
            };

            if (isInWarranty || shouldOpenDialog) {
                warrantyPatch.mainChassisPolicy = 0;
            }

            setIsVehicleWarrantyInfLocked(true);
            setFields({
                ...warrantyPatch,
                ...(!isInWarranty &&
                    shouldOpenDialog && {
                        hasScrolledToBottomWarranty: false,
                        warrantyPolicyDialogVisible: true,
                        warrantyPolicyDialogContext: 'main'
                    })
            });
        };

        resolveWarrantyInfo();
        return () => {
            isCancelled = true;
        };
    }, [dialogVisible, form.chassisNo, form.errorNotificationDate, editMode, setFields, checkChassisWarranty, form.initialChassisNo]);

    useEffect(() => {
        if (!dialogVisible) return;

        const chassisNo = String(form.chassisNo || '').trim();
        if (!chassisNo || isFetchingSelectedChassis) return;

        const selectedChassis = selectedChassisCandidates.find((item) => String(item?.id ?? item?.chassisNo ?? '').trim() === chassisNo);
        const modelValue = resolveVehicleModelFromChassis(selectedChassis);

        if (modelValue) {
            setField('vehicleModelEmissions', modelValue);
            setIsVehicleModelLocked(true);
            return;
        }

        setIsVehicleModelLocked(false);
    }, [dialogVisible, form.chassisNo, isFetchingSelectedChassis, selectedChassisCandidates, setField]);

    useEffect(() => {
        if (!dialogVisible) return;

        if (!form.moreChassis && form.additionalChassis?.length) {
            setFields({
                additionalChassis: [],
                additionalChassisHasOutOfWarranty: false,
                additionalChassisWarrantyPolicyAccepted: false
            });
        } else if (!form.moreChassis && form.additionalChassisHasOutOfWarranty) {
            setFields({
                additionalChassisHasOutOfWarranty: false
            });
        }
    }, [dialogVisible, form.moreChassis, form.additionalChassis, form.additionalChassisHasOutOfWarranty, setFields]);

    useEffect(() => {
        if (!dialogVisible) return;
        if (!form.onSiteSupport) {
            const needsClear =
                (form.onSiteSupportReason || []).length > 0 ||
                (form.technicalServiceDeclaration || []).length > 0 ||
                form.technicalPolicy !== false ||
                form.visitDate !== null ||
                (form.visitErrorDesc || '') !== '' ||
                (form.rootCause || '') !== '' ||
                (form.solution || '') !== '' ||
                form.manHour !== null ||
                form.outsourcingExpense !== null ||
                form.totalExpenses !== null;

            if (needsClear) {
                setFields({
                    onSiteSupportReason: [],
                    technicalServiceDeclaration: [],
                    technicalPolicy: false,
                    visitDate: null,
                    visitErrorDesc: '',
                    rootCause: '',
                    solution: '',
                    manHour: null,
                    outsourcingExpense: null,
                    totalExpenses: null
                });
            }
        }
    }, [
        dialogVisible,
        form.onSiteSupport,
        form.onSiteSupportReason,
        form.technicalServiceDeclaration,
        form.technicalPolicy,
        form.visitDate,
        form.visitErrorDesc,
        form.rootCause,
        form.solution,
        form.manHour,
        form.outsourcingExpense,
        form.totalExpenses,
        setFields
    ]);

    const uploadExpenseFiles = useCallback(
        async (requestId) => {
            if (!requestId || expenseFiles.length === 0) return true;

            const failedFiles = [];
            for (const item of expenseFiles) {
                const f = item?.rawFile || item?.file || item;
                try {
                    await uploadExpenseDocument({
                        TechnicalSupportRequestId: requestId,
                        file: f,
                        description: f?.name || item?.name || 'attachment'
                    }).unwrap();
                } catch (error) {
                    failedFiles.push(item);
                    toastService.error(error?.data?.errorMessage || th('technicalSupport.messages.uploadDocumentError'));
                }
            }
            if (request?.id) {
                try {
                    await refetchExpenseDocuments();
                } catch {
                    // Query henüz başlamadıysa veya başka bir sebepten sessizce geç
                }
            }
            setExpenseFiles(failedFiles);
            return failedFiles.length === 0;
        },
        [expenseFiles, uploadExpenseDocument, th, request?.id, refetchExpenseDocuments]
    );

    const doSave = useCallback(
        async (payload) => {
            setIsSaving(true);
            try {
                let requestId;
                if (editMode && request?.id) {
                    await updateRequest(payload).unwrap();
                    requestId = request.id;
                } else {
                    const result = await createRequest(payload).unwrap();
                    requestId = result?.data ?? result?.id;
                }

                const documentsUploaded = requestId && updatedFiles.length > 0 ? await uploadFiles(requestId) : true;
                const expenseDocumentsUploaded = requestId && expenseFiles.length > 0 ? await uploadExpenseFiles(requestId) : true;

                if (!documentsUploaded || !expenseDocumentsUploaded) return null;

                onSaveSuccess();
                return requestId;
            } catch (error) {
                toastService.error(error?.data?.errorMessage);
                return null;
            } finally {
                setIsSaving(false);
            }
        },
        [editMode, request, updateRequest, createRequest, onSaveSuccess, updatedFiles, uploadFiles, expenseFiles, uploadExpenseFiles]
    );

    const handleFeedbackSubmit = useCallback(
        async (answers) => {
            const requestId = await doSave(pendingPayload);
            if (!requestId) return;

            try {
                await saveFeedbackAnswers({
                    technicalSupId: requestId,
                    answers
                }).unwrap();
            } catch {
                // ignore
            }

            setFeedbackVisible(false);
            setPendingPayload(null);
        },
        [doSave, pendingPayload, saveFeedbackAnswers]
    );

    const handleSubmit = useCallback(async () => {
        setField('submitted', true);

        if (shouldCheckPreviousKm && chassisNo && (isFetchingPreviousKm || !previousKmResponse || previousKmResponse.isSuccessful === false || isPreviousKmError)) {
            toastService.error(th('technicalSupport.previousKmUnavailable'));
            return;
        }

        if (isKmBelowPrevious) return;

        if (!validate()) {
            if (form.onSiteSupport && !form.technicalPolicy && technicalPolicyRef.current) {
                technicalPolicyRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            if (form.additionalChassisHasOutOfWarranty && !form.additionalChassisWarrantyPolicyAccepted && additionalWarrantyPolicyRef.current) {
                additionalWarrantyPolicyRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return;
        }

        const payload = mapFormToPayload(form, editMode, request, userParams, isVorMode);

        const shouldShowFeedback = isDealer && form.problemSolved && form.solutionDate && form.solutionMethod?.trim();

        if (shouldShowFeedback) {
            setPendingPayload(payload);
            setFeedbackVisible(true);
            return;
        }

        await doSave(payload);
    }, [validate, form, setField, mapFormToPayload, editMode, request, userParams, isVorMode, isDealer, doSave, chassisNo, shouldCheckPreviousKm, isFetchingPreviousKm, previousKmResponse, isPreviousKmError, isKmBelowPrevious, th]);

    const onDeleteExpenseDocument = useCallback(
        async (doc) => {
            const docId = getDocId(doc);
            const requestId = request?.id ?? request?.Id ?? null;

            if (!requestId || !docId) {
                toastService.error(th('technicalSupport.messages.missingFileInfo'));
                return;
            }

            const fileName = doc.fileName || doc.name || 'File';
            if (!window.confirm(th('technicalSupport.deleteDocumentConfirm', { fileName }))) return;

            try {
                await deleteExpenseDocument({ Id: Number(requestId), DocumentId: Number(docId) }).unwrap();
                await refetchExpenseDocuments();
                toastService.success(th('technicalSupport.messages.deleteDocumentSuccess'));
            } catch (error) {
                toastService.error(error?.data?.errorMessage || th('technicalSupport.messages.deleteDocumentError'));
            }
        },
        [request, th, deleteExpenseDocument, refetchExpenseDocuments]
    );

    const handleSendMessage = useCallback(async () => {
        // Boş editör '<p><br></p>' üretir; .trim() ile kontrol yanlış sonuç verir.
        if (!isEmptyHtmlContent(form.newMessage) && request?.id) {
            try {
                const response = await addComment({
                    ticketId: request.id,
                    type: 1,
                    // HTML kırpılmaz; boşluk normalizasyonunu sunucu yapar.
                    comment: form.newMessage,
                    createdName: userParams?.userEmail || 'System',
                    createdSession: 'web'
                }).unwrap();

                toastService.success(th('technicalSupport.messages.sendMessageSuccess'));

                if (onRefreshRequest) {
                    onRefreshRequest();
                }

                return getCreatedCommentId(response);
            } catch (error) {
                const errorMessage = error?.data?.errorMessage || th('technicalSupport.messages.unknownError');
                toastService.error(`${th('technicalSupport.messages.sendMessageError')}: ${errorMessage}`);
                return null;
            }
        }
        return null;
    }, [form.newMessage, request, addComment, userParams, onRefreshRequest, th]);

    const handleFaultCodeChange = useCallback(
        (field, value) => {
            const extractName = (displayValue) => {
                if (!displayValue) return '';
                const parts = String(displayValue).split(' - ');
                return parts.length > 1 ? parts.slice(1).join(' - ') : '';
            };

            const nameField = field === 'mainCategory' ? 'mainCategoryName' : field === 'secondCategory' ? 'secondCategoryName' : field === 'subCategory' ? 'subCategoryName' : field === 'subPartCategory' ? 'subPartCategoryName' : null;

            if (nameField) {
                setFields({
                    [field]: value,
                    [nameField]: extractName(value)
                });
            } else {
                setField(field, value);
            }
        },
        [setField, setFields]
    );

    const handleFailureModeChange = useCallback(
        (value) => {
            setField('failureMode', value);
        },
        [setField]
    );

    const handleAdditionalChassisChange = useCallback(
        async (selectedChassisList) => {
            const newList = selectedChassisList || [];
            const prevList = form.additionalChassis || [];
            const addedChassis = newList.filter((c) => !prevList.includes(c));
            const isRemoval = addedChassis.length === 0;

            setField('additionalChassis', newList);

            if (newList.length === 0) {
                setFields({
                    additionalChassisHasOutOfWarranty: false,
                    additionalChassisWarrantyPolicyAccepted: false
                });
                return;
            }

            if (isRemoval) {
                let hasOutOfWarranty = false;
                for (const chassisNo of newList) {
                    const isInWarranty = await checkChassisWarranty(chassisNo, form.errorNotificationDate);

                    if (!isInWarranty) {
                        hasOutOfWarranty = true;
                        break;
                    }
                }

                if (!hasOutOfWarranty) {
                    setFields({
                        additionalChassisHasOutOfWarranty: false,
                        additionalChassisWarrantyPolicyAccepted: false
                    });
                }
                return;
            }

            for (const chassisNo of addedChassis) {
                const isInWarranty = await checkChassisWarranty(chassisNo, form.errorNotificationDate);

                if (!isInWarranty) {
                    setFields({
                        additionalChassisHasOutOfWarranty: true,
                        additionalChassisWarrantyPolicyAccepted: false,
                        hasScrolledToBottomWarranty: false
                    });
                    return;
                }
            }
        },
        [form.additionalChassis, form.errorNotificationDate, setField, setFields, checkChassisWarranty]
    );

    const handleFeedbackHide = useCallback(() => {
        setFeedbackVisible(false);
        if (pendingPayload) {
            doSave(pendingPayload);
            setPendingPayload(null);
        }
    }, [pendingPayload, doSave]);

    const handleWarrantyPolicyAccept = useCallback(() => {
        if (form.warrantyPolicyDialogContext === 'main') {
            setFields({ mainChassisPolicy: 1, warrantyPolicyDialogVisible: false });
        } else {
            setFields({ additionalChassisWarrantyPolicyAccepted: true, warrantyPolicyDialogVisible: false });
        }
    }, [form.warrantyPolicyDialogContext, setFields]);

    return {
        th,
        userParams,
        form,
        problemSolvedRef,
        releasedStatusCode,
        changeDateRequired,
        setField,
        handleActionManagerChange,
        handleStatusChange,
        handleProblemSolvedChange,
        handleStatusDateChange,
        scrollToProblemSolved,
        handleSolutionDateChange,
        handleChassisChange,
        setFields,
        request,
        editMode,
        dialogVisible,
        isDealer,
        isVorMode,
        isReadOnly,
        shouldShowDealerAddressSelection,
        isSaving,
        previousKm,
        formattedPreviousKm,
        previousKmUnit,
        isKmBelowPrevious,
        isKmInvalid,
        isFetchingPreviousKm,
        isPreviousKmError,
        isAddingComment,
        isFetchingWarrantyInformation,
        isVehicleWarrantyInfLocked,
        isVehicleModelLocked,
        warrantyOptions,
        gsrOptions,
        menzilTypeOptions,
        errorEffectOptions,
        vehicleEmissionOptions,
        selectedErrorEffectInfo,
        additionalChassisOptions,
        technicalResponsibleOptions,
        isFetchingTechnicalResponsibles,
        selectedSiteUseId,
        canEditTechnicalResponsible,
        isVisitDateAfterErrorNotificationInvalid,
        isSolutionDateAfterErrorNotificationInvalid,
        dealerCustomerFiles,
        technicalTeamFiles,
        documentTabIndex,
        expenseDocuments,
        updatedFiles,
        expenseFiles,
        isLoadingDocuments,
        isDownloadingDocuments,
        isLoadingExpenseDocuments,
        isFetchingAdditionalChassis,
        debouncedChassisSearch,
        feedbackVisible,
        toastRef,
        technicalPolicyRef,
        additionalWarrantyPolicyRef,
        setAdditionalChassisSearch,
        setExpenseFiles,
        setDocumentTabIndex,
        onEditFilesChange,
        onDownloadDocument,
        onDownloadAllDocuments,
        onDeleteDocument,
        onDeleteExpenseDocument,
        handleSubmit,
        handleSendMessage,
        handleFaultCodeChange,
        handleFailureModeChange,
        handleAdditionalChassisChange,
        handleFeedbackSubmit,
        handleFeedbackHide,
        handleWarrantyPolicyAccept
    };
};

export default useTechnicalSupportRequestFormData;
